import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, FolderOpen, Plus, Pencil, ChevronRight, AlertCircle, RotateCcw, CheckCircle2, CalendarDays, Archive, Layers, Users as UsersIcon, UserPlus } from "lucide-react";
import { ScreenShell, GlassCard, GradientText, SimHeader } from "@/components/sim/shared";
import { AdminNav } from "@/components/neusim/AdminNav";
import { CreateCohortModal } from "@/components/admin/CreateCohortModal";
import { apiCall, ApiError } from "@/lib/api";

interface ClientRow {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  createdAt: string;
  projectCount: number;
  simCount: number;
}

interface ProjectRow {
  id: string;
  clientId: string;
  clientName: string;
  name: string;
  slug: string;
  accessStart: string | null;
  accessEnd: string | null;
  isActive: boolean;
  createdAt: string;
  simCount: number;
}

interface CohortRow {
  id: string;
  name: string;
  isActive: boolean;
  project: { id: string; name: string };
  client: { id: string; name: string };
  memberCount: number;
  activeMemberCount: number;
  simCount: number;
  activeSimCount: number;
}

interface UserRow {
  id: string;
  email: string;
  fullName: string;
  role: string;
  clientId: string | null;
  clientName: string | null;
  isActive: boolean;
}

function fmtDate(iso: string | null): string {
  if (iso === null) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * AdminClientsPage — /admin/clients — Step 9 tenancy admin.
 *
 * One row per client with aggregate counts. Click the row to expand into the
 * client's projects (lazy-fetched once on first open). Per-row Edit modal
 * (for both client and project) handles rename + soft-archive + access
 * window (projects only).
 *
 * Super_admin only — enforced server-side on every endpoint.
 */
export function AdminClientsPage(): JSX.Element {
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openClientId, setOpenClientId] = useState<string | null>(null);
  const [projectsByClient, setProjectsByClient] = useState<Record<string, ProjectRow[]>>({});
  const [projectsLoading, setProjectsLoading] = useState<string | null>(null);

  const [editingClient, setEditingClient] = useState<ClientRow | "new" | null>(null);
  const [editingProject, setEditingProject] = useState<{ clientId: string; project: ProjectRow | "new" } | null>(null);

  // Phase M1: cohorts + users per client, lazy-loaded on expand.
  const [cohortsByClient, setCohortsByClient] = useState<Record<string, CohortRow[]>>({});
  const [usersByClient, setUsersByClient] = useState<Record<string, UserRow[]>>({});
  const [creatingCohortForClient, setCreatingCohortForClient] = useState<string | null>(null);

  const loadClients = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall<{ clients: ClientRow[] }>("/libraryClients");
      setClients(res.clients);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't load clients");
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async (clientId: string): Promise<void> => {
    setProjectsLoading(clientId);
    try {
      const res = await apiCall<{ projects: ProjectRow[] }>("/libraryProjects", {
        method: "POST",
        body: { clientId },
        timeoutMs: 120_000,
      });
      setProjectsByClient((prev) => ({ ...prev, [clientId]: res.projects }));
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't load projects";
      setProjectsByClient((prev) => ({ ...prev, [clientId]: [] }));
      console.error("loadProjects:", msg);
    } finally {
      setProjectsLoading(null);
    }
  };

  // Phase M1: cohorts + users in the client expansion.
  const loadCohorts = async (clientId: string): Promise<void> => {
    try {
      const res = await apiCall<{ cohorts: CohortRow[] }>("/libraryCohortList", {
        method: "POST", body: { clientId }, timeoutMs: 120_000,
      });
      setCohortsByClient((prev) => ({ ...prev, [clientId]: res.cohorts }));
    } catch (err) {
      setCohortsByClient((prev) => ({ ...prev, [clientId]: [] }));
      console.error("loadCohorts:", err);
    }
  };

  const loadUsers = async (clientId: string): Promise<void> => {
    try {
      const res = await apiCall<{ users: UserRow[] }>("/libraryUsers", {
        method: "POST", body: { clientId, includeArchived: false }, timeoutMs: 120_000,
      });
      setUsersByClient((prev) => ({ ...prev, [clientId]: res.users }));
    } catch (err) {
      setUsersByClient((prev) => ({ ...prev, [clientId]: [] }));
      console.error("loadUsers:", err);
    }
  };

  useEffect(() => { void loadClients(); }, []);

  const toggleExpand = (c: ClientRow): void => {
    if (openClientId === c.id) { setOpenClientId(null); return; }
    setOpenClientId(c.id);
    if (projectsByClient[c.id] === undefined) void loadProjects(c.id);
    if (cohortsByClient[c.id] === undefined) void loadCohorts(c.id);
    if (usersByClient[c.id] === undefined) void loadUsers(c.id);
  };

  return (
    <ScreenShell>
      <SimHeader decision={clients?.length ?? 0} xp={0} />
      <main className="mx-auto max-w-6xl px-4 pb-20 md:px-8">
        <div className="mb-5"><AdminNav /></div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-fuchsia-300">
                <Building2 className="h-4 w-4" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em]">Tenancy</span>
              </div>
              <h1 className="mt-2 text-[28px] font-bold leading-tight">
                <GradientText>Clients &amp; projects</GradientText>
              </h1>
              <p className="mt-1 text-sm text-white/55">Create the tenant a sim lives under. Click a client to manage its projects.</p>
            </div>
            <button
              type="button"
              onClick={() => setEditingClient("new")}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl border border-fuchsia-400/40 bg-fuchsia-500/15 px-4 py-2 text-sm font-semibold text-fuchsia-100 hover:border-fuchsia-400/60 hover:bg-fuchsia-500/25"
            >
              <Plus className="h-4 w-4" /> New client
            </button>
          </div>
        </motion.div>

        {loading && <p className="mt-8 text-sm text-white/45">Loading…</p>}
        {!loading && error !== null && (
          <GlassCard className="mx-auto mt-8 max-w-lg p-8 text-center">
            <AlertCircle className="mx-auto h-9 w-9 text-[#f87272]" />
            <p className="mt-3 text-sm text-white/70">{error}</p>
            <button type="button" onClick={() => void loadClients()} className="mt-5 inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white hover:border-white/30">
              <RotateCcw className="h-3.5 w-3.5" /> Try again
            </button>
          </GlassCard>
        )}

        {!loading && error === null && clients !== null && clients.length === 0 && (
          <GlassCard className="mt-8 p-10 text-center">
            <p className="text-sm text-white/55">No clients yet. Create one to get started.</p>
          </GlassCard>
        )}

        {!loading && error === null && clients !== null && clients.length > 0 && (
          <GlassCard className="mt-6 overflow-hidden p-0">
            <table className="w-full table-fixed text-[12.5px]">
              <colgroup>
                <col style={{ width: "2.5rem" }} />
                <col />
                <col style={{ width: "6rem" }} />
                <col style={{ width: "5rem" }} />
                <col style={{ width: "5rem" }} />
                <col style={{ width: "5.5rem" }} />
                <col style={{ width: "5rem" }} />
              </colgroup>
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.02] text-[10px] uppercase tracking-[0.12em] text-white/40">
                  <th></th>
                  <th className="px-3 py-2.5 text-left">Client</th>
                  <th className="px-3 py-2.5 text-left">Slug</th>
                  <th className="px-3 py-2.5 text-right">Projects</th>
                  <th className="px-3 py-2.5 text-right">Sims</th>
                  <th className="px-3 py-2.5 text-left">Status</th>
                  <th className="px-3 py-2.5 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => {
                  const isOpen = openClientId === c.id;
                  const projects = projectsByClient[c.id] ?? null;
                  return (
                    <RowGroup key={c.id}>
                      <tr
                        onClick={() => toggleExpand(c)}
                        className={`cursor-pointer border-b border-white/5 hover:bg-white/[0.03] ${!c.isActive ? "opacity-50" : ""}`}
                      >
                        <td className="px-1 py-2.5 text-center text-white/40">
                          <ChevronRight className={`inline h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                        </td>
                        <td className="px-3 py-2.5 truncate font-medium text-white">{c.name}</td>
                        <td className="px-3 py-2.5 font-mono text-[11px] text-white/55">{c.slug}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-white/80">{c.projectCount}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-white/80">{c.simCount}</td>
                        <td className="px-3 py-2.5 text-left">
                          {c.isActive ? (
                            <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-300">Active</span>
                          ) : (
                            <span className="rounded-full border border-white/15 bg-white/[0.05] px-2 py-0.5 text-[10px] font-bold uppercase text-white/45">Archived</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <button type="button" onClick={(e) => { e.stopPropagation(); setEditingClient(c); }} className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-[10.5px] text-white/75 hover:border-white/30">
                            <Pencil className="h-3 w-3" /> Edit
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-white/5 bg-white/[0.02]">
                          <td></td>
                          <td colSpan={6} className="space-y-5 px-3 py-3">

                            {/* Projects */}
                            <div>
                              <div className="flex items-center justify-between gap-3">
                                <h3 className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-cyan-200">
                                  <FolderOpen className="mr-1 inline h-3 w-3" /> Projects ({projects?.length ?? 0})
                                </h3>
                                <button type="button" onClick={() => setEditingProject({ clientId: c.id, project: "new" })} className="inline-flex items-center gap-1 rounded-md border border-fuchsia-400/30 bg-fuchsia-500/10 px-2 py-1 text-[10.5px] font-semibold text-fuchsia-200 hover:border-fuchsia-400/60 hover:bg-fuchsia-500/15">
                                  <Plus className="h-3 w-3" /> New project
                                </button>
                              </div>
                              {projectsLoading === c.id && <p className="mt-2 text-[11px] text-white/45">Loading projects…</p>}
                              {projectsLoading !== c.id && projects !== null && projects.length === 0 && (
                                <p className="mt-2 text-[11px] text-white/45">No projects yet in this client.</p>
                              )}
                              {projects !== null && projects.length > 0 && (
                                <div className="mt-2 space-y-1.5">
                                  {projects.map((p) => (
                                    <div key={p.id} className={`grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-3 rounded-md border border-white/8 bg-white/[0.02] px-3 py-2 ${!p.isActive ? "opacity-50" : ""}`}>
                                      <div className="min-w-0">
                                        <div className="truncate text-[12px] font-medium text-white">{p.name}</div>
                                        <div className="truncate text-[10px] font-mono text-white/40">{p.slug}</div>
                                      </div>
                                      <span className="font-mono text-[11px] text-white/65">{p.simCount} sims</span>
                                      <span className="inline-flex items-center gap-1 text-[10.5px] text-white/50" title={`${fmtDate(p.accessStart)} → ${fmtDate(p.accessEnd)}`}>
                                        <CalendarDays className="h-3 w-3" /> {fmtDate(p.accessStart)} – {fmtDate(p.accessEnd)}
                                      </span>
                                      {p.isActive ? (
                                        <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-300">Active</span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.05] px-1.5 py-0.5 text-[9px] font-bold uppercase text-white/45">
                                          <Archive className="h-2.5 w-2.5" /> Archived
                                        </span>
                                      )}
                                      <button type="button" onClick={() => setEditingProject({ clientId: c.id, project: p })} className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-[10.5px] text-white/75 hover:border-white/30">
                                        <Pencil className="h-3 w-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Cohorts */}
                            <div>
                              <div className="flex items-center justify-between gap-3">
                                <h3 className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-emerald-200">
                                  <Layers className="mr-1 inline h-3 w-3" /> Cohorts ({cohortsByClient[c.id]?.length ?? 0})
                                </h3>
                                <button type="button" onClick={() => setCreatingCohortForClient(c.id)} className="inline-flex items-center gap-1 rounded-md border border-fuchsia-400/30 bg-fuchsia-500/10 px-2 py-1 text-[10.5px] font-semibold text-fuchsia-200 hover:border-fuchsia-400/60 hover:bg-fuchsia-500/15">
                                  <Plus className="h-3 w-3" /> New cohort
                                </button>
                              </div>
                              {cohortsByClient[c.id] === undefined && <p className="mt-2 text-[11px] text-white/45">Loading cohorts…</p>}
                              {cohortsByClient[c.id] !== undefined && cohortsByClient[c.id]!.length === 0 && (
                                <p className="mt-2 text-[11px] text-white/45">No cohorts yet. Create one to start grouping learners under a project.</p>
                              )}
                              {cohortsByClient[c.id] !== undefined && cohortsByClient[c.id]!.length > 0 && (
                                <div className="mt-2 space-y-1.5">
                                  {cohortsByClient[c.id]!.map((co) => (
                                    <div key={co.id} className={`grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-3 rounded-md border border-white/8 bg-white/[0.02] px-3 py-2 ${!co.isActive ? "opacity-50" : ""}`}>
                                      <div className="min-w-0">
                                        <div className="truncate text-[12px] font-medium text-white">{co.name}</div>
                                        <div className="truncate text-[10px] text-white/40">{co.project.name}</div>
                                      </div>
                                      <span className="font-mono text-[11px] text-white/65">{co.activeMemberCount} members</span>
                                      <span className="font-mono text-[11px] text-white/65">{co.activeSimCount} sims</span>
                                      {co.isActive ? (
                                        <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-300">Active</span>
                                      ) : (
                                        <span className="rounded-full border border-white/15 bg-white/[0.05] px-1.5 py-0.5 text-[9px] font-bold uppercase text-white/45">Archived</span>
                                      )}
                                      <button type="button" onClick={() => navigate("/admin/cohorts")} className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-[10.5px] text-white/75 hover:border-white/30" title="Manage in Cohorts">
                                        <Pencil className="h-3 w-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Users */}
                            <div>
                              <div className="flex items-center justify-between gap-3">
                                <h3 className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-violet-200">
                                  <UsersIcon className="mr-1 inline h-3 w-3" /> Users ({usersByClient[c.id]?.length ?? 0})
                                </h3>
                                <button type="button" onClick={() => navigate("/admin/users")} className="inline-flex items-center gap-1 rounded-md border border-fuchsia-400/30 bg-fuchsia-500/10 px-2 py-1 text-[10.5px] font-semibold text-fuchsia-200 hover:border-fuchsia-400/60 hover:bg-fuchsia-500/15">
                                  <UserPlus className="h-3 w-3" /> New user
                                </button>
                              </div>
                              {usersByClient[c.id] === undefined && <p className="mt-2 text-[11px] text-white/45">Loading users…</p>}
                              {usersByClient[c.id] !== undefined && usersByClient[c.id]!.length === 0 && (
                                <p className="mt-2 text-[11px] text-white/45">No users assigned to this client yet.</p>
                              )}
                              {usersByClient[c.id] !== undefined && usersByClient[c.id]!.length > 0 && (
                                <div className="mt-2 space-y-1.5">
                                  {usersByClient[c.id]!.slice(0, 10).map((u) => (
                                    <div key={u.id} className={`grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 rounded-md border border-white/8 bg-white/[0.02] px-3 py-2 ${!u.isActive ? "opacity-50" : ""}`}>
                                      <div className="min-w-0">
                                        <div className="truncate text-[12px] font-medium text-white">{u.fullName}</div>
                                        <div className="truncate text-[10px] text-white/40">{u.email}</div>
                                      </div>
                                      <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${u.role === "client_admin" ? "border border-violet-400/30 bg-violet-500/10 text-violet-200" : u.role === "super_admin" ? "border border-emerald-400/30 bg-emerald-500/10 text-emerald-200" : "border border-white/15 bg-white/[0.05] text-white/50"}`}>
                                        {u.role.replace("_", " ")}
                                      </span>
                                      {u.isActive ? (
                                        <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-300">Active</span>
                                      ) : (
                                        <span className="rounded-full border border-white/15 bg-white/[0.05] px-1.5 py-0.5 text-[9px] font-bold uppercase text-white/45">Archived</span>
                                      )}
                                      <button type="button" onClick={() => navigate("/admin/users")} className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-[10.5px] text-white/75 hover:border-white/30" title="Manage in Users">
                                        <Pencil className="h-3 w-3" />
                                      </button>
                                    </div>
                                  ))}
                                  {usersByClient[c.id]!.length > 10 && (
                                    <button type="button" onClick={() => navigate("/admin/users")} className="w-full rounded-md border border-white/8 bg-white/[0.02] px-3 py-2 text-[11px] text-white/55 hover:bg-white/5">
                                      + {usersByClient[c.id]!.length - 10} more · open Users
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>

                          </td>
                        </tr>
                      )}
                    </RowGroup>
                  );
                })}
              </tbody>
            </table>
          </GlassCard>
        )}

        {editingClient !== null && (
          <ClientEditModal
            initial={editingClient === "new" ? null : editingClient}
            onClose={() => setEditingClient(null)}
            onSaved={() => { setEditingClient(null); void loadClients(); }}
          />
        )}

        {editingProject !== null && (
          <ProjectEditModal
            clientId={editingProject.clientId}
            initial={editingProject.project === "new" ? null : editingProject.project}
            onClose={() => setEditingProject(null)}
            onSaved={() => {
              const cid = editingProject.clientId;
              setEditingProject(null);
              void loadProjects(cid);
              void loadClients();
            }}
          />
        )}

        {creatingCohortForClient !== null && (
          <CreateCohortModal
            scopeClientId={creatingCohortForClient}
            onClose={() => setCreatingCohortForClient(null)}
            onCreated={() => {
              const cid = creatingCohortForClient;
              setCreatingCohortForClient(null);
              void loadCohorts(cid);
            }}
          />
        )}
      </main>
    </ScreenShell>
  );
}

function RowGroup({ children }: { children: React.ReactNode }): JSX.Element { return <>{children}</>; }

function ClientEditModal({ initial, onClose, onSaved }: { initial: ClientRow | null; onClose: () => void; onSaved: () => void }): JSX.Element {
  const [name, setName] = useState(initial?.name ?? "");
  const [slug, setSlug] = useState(initial?.slug ?? "");
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (): Promise<void> => {
    if (name.trim() === "") { setError("Name is required"); return; }
    setBusy(true); setError(null);
    try {
      await apiCall("/libraryClientSave", {
        method: "POST",
        body: initial === null
          ? { name, slug: slug.trim() === "" ? undefined : slug }
          : { id: initial.id, name, slug: slug.trim() === "" ? undefined : slug, isActive },
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal title={initial === null ? "New client" : `Edit ${initial.name}`} onClose={onClose} busy={busy}>
      <div className="space-y-3">
        <ModalField label="Name *">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Accenture" className={inputClass} />
        </ModalField>
        <ModalField label="Slug" hint="Lowercase, hyphens. Auto-derived from name if blank.">
          <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto" className={`${inputClass} font-mono text-[12px]`} />
        </ModalField>
        {initial !== null && (
          <label className="flex items-center gap-2 text-[12px] text-white/65">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="accent-fuchsia-400" />
            Active — uncheck to archive (sims stay accessible; client is hidden from new authoring)
          </label>
        )}
        {error !== null && <p className="text-[11px] text-rose-300"><AlertCircle className="mr-1 inline h-3 w-3" />{error}</p>}
      </div>
      <ModalFooter onCancel={onClose} onSave={() => void handleSave()} busy={busy} saveLabel={initial === null ? "Create client" : "Save changes"} />
    </Modal>
  );
}

function ProjectEditModal({ clientId, initial, onClose, onSaved }: { clientId: string; initial: ProjectRow | null; onClose: () => void; onSaved: () => void }): JSX.Element {
  const [name, setName] = useState(initial?.name ?? "");
  const [slug, setSlug] = useState(initial?.slug ?? "");
  const [accessStart, setAccessStart] = useState(initial?.accessStart?.slice(0, 10) ?? "");
  const [accessEnd, setAccessEnd] = useState(initial?.accessEnd?.slice(0, 10) ?? "");
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (): Promise<void> => {
    if (name.trim() === "") { setError("Name is required"); return; }
    setBusy(true); setError(null);
    try {
      await apiCall("/libraryProjectSave", {
        method: "POST",
        body: initial === null
          ? { clientId, name, slug: slug.trim() === "" ? undefined : slug, accessStart: accessStart === "" ? null : accessStart, accessEnd: accessEnd === "" ? null : accessEnd }
          : { id: initial.id, name, slug: slug.trim() === "" ? undefined : slug, accessStart: accessStart === "" ? null : accessStart, accessEnd: accessEnd === "" ? null : accessEnd, isActive },
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal title={initial === null ? "New project" : `Edit ${initial.name}`} onClose={onClose} busy={busy}>
      <div className="space-y-3">
        <ModalField label="Name *"><input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. AMP 2026 Cohort A" className={inputClass} /></ModalField>
        <ModalField label="Slug" hint="Per-client unique. Auto-derived from name if blank.">
          <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto" className={`${inputClass} font-mono text-[12px]`} />
        </ModalField>
        <div className="grid grid-cols-2 gap-3">
          <ModalField label="Access start"><input type="date" value={accessStart} onChange={(e) => setAccessStart(e.target.value)} className={inputClass} /></ModalField>
          <ModalField label="Access end"><input type="date" value={accessEnd} onChange={(e) => setAccessEnd(e.target.value)} className={inputClass} /></ModalField>
        </div>
        {initial !== null && (
          <label className="flex items-center gap-2 text-[12px] text-white/65">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="accent-fuchsia-400" />
            Active — uncheck to archive (existing learner attempts unaffected)
          </label>
        )}
        {error !== null && <p className="text-[11px] text-rose-300"><AlertCircle className="mr-1 inline h-3 w-3" />{error}</p>}
      </div>
      <ModalFooter onCancel={onClose} onSave={() => void handleSave()} busy={busy} saveLabel={initial === null ? "Create project" : "Save changes"} />
    </Modal>
  );
}

const inputClass = "w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-fuchsia-400/60";

function ModalField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/55">{label}</label>
      {hint !== undefined && <span className="ml-2 text-[10px] text-white/35">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Modal({ title, busy, onClose, children }: { title: string; busy: boolean; onClose: () => void; children: React.ReactNode }): JSX.Element {
  useEffect(() => {
    const h = (e: KeyboardEvent): void => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, [busy, onClose]);
  return (
    <div onClick={() => { if (!busy) onClose(); }} className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm px-4 py-10">
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#0c0e22] to-[#070818] text-white shadow-2xl">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-400" />
        <div className="border-b border-white/10 px-6 py-4"><h2 className="text-lg font-bold">{title}</h2></div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({ onCancel, onSave, busy, saveLabel }: { onCancel: () => void; onSave: () => void; busy: boolean; saveLabel: string }): JSX.Element {
  return (
    <div className="mt-5 flex items-center justify-end gap-2 border-t border-white/10 pt-4">
      <button type="button" onClick={onCancel} disabled={busy} className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-[12px] text-white/70 hover:border-white/30 disabled:opacity-40">Cancel</button>
      <button type="button" onClick={onSave} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/15 px-4 py-1.5 text-[12px] font-semibold text-fuchsia-100 hover:border-fuchsia-400/60 hover:bg-fuchsia-500/25 disabled:cursor-not-allowed disabled:opacity-40">
        {busy ? <RotateCcw className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
        {busy ? "Saving…" : saveLabel}
      </button>
    </div>
  );
}
